
import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'}`}>
        <div className="px-16 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://public.readdy.ai/ai/img_res/85a73544-7acc-486b-93ea-d43e3a2ab79a.png" alt="SUALCAN SL Logo" className="h-10 w-10 object-contain" />
            <span className={`text-xl font-medium tracking-wide ${scrolled ? 'text-gray-900' : 'text-white'}`}>SUALCAN SL</span>
          </div>
          <div className="flex items-center gap-10">
            <button onClick={() => scrollToSection('home')} className={`text-base tracking-wider transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-900 hover:text-gray-600' : 'text-white hover:text-gray-200'}`}>Home</button>
            <button onClick={() => scrollToSection('about')} className={`text-base tracking-wider transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-900 hover:text-gray-600' : 'text-white hover:text-gray-200'}`}>About</button>
            <button onClick={() => scrollToSection('products')} className={`text-base tracking-wider transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-900 hover:text-gray-600' : 'text-white hover:text-gray-200'}`}>Products</button>
            <button onClick={() => scrollToSection('contact')} className={`text-base tracking-wider transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-900 hover:text-gray-600' : 'text-white hover:text-gray-200'}`}>Contact</button>
          </div>
        </div>
        <div className={`h-px ${scrolled ? 'bg-gray-200' : 'bg-white/10'}`}></div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/40"></div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 right-20 w-96 h-96 bg-blue-500/30 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-purple-500/30 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative w-full px-16 flex items-center">
          <div className="w-2/5 pl-8">
            <h1 className="text-7xl font-bold text-white leading-tight mb-20">
              <div>Crafting Premium</div>
              <div>iOS Experiences</div>
              <div>For Modern Users</div>
            </h1>
            <p className="text-2xl text-white/60 font-light max-w-lg">Security-focused applications designed for the iOS ecosystem</p>
          </div>
          
          <div className="w-3/5 flex justify-end pr-10">
            <div className="w-full max-w-3xl h-[600px] rounded-[32px] bg-gradient-to-br from-purple-900 via-blue-600 to-blue-500 p-12 relative overflow-hidden shadow-2xl">
              <div className="absolute top-20 right-20 w-64 h-64 bg-white/10 rounded-full blur-2xl"></div>
              <div className="absolute bottom-20 left-20 w-80 h-80 bg-white/5 rounded-3xl blur-3xl"></div>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white/5 rounded-full blur-2xl"></div>
              
              <div className="absolute bottom-12 left-12">
                <div className="inline-block px-6 py-3 bg-white/20 backdrop-blur-md rounded-full border border-white/30">
                  <span className="text-white font-medium text-sm tracking-wide">iOS Focused</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="absolute top-10 right-32">
          <svg width="120" height="120" viewBox="0 0 120 120" fill="none" className="opacity-20">
            <path d="M20 60 Q 60 20, 100 60" stroke="white" strokeWidth="2" fill="none"/>
          </svg>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-40 bg-[#F8F6F3]">
        <div className="max-w-7xl mx-auto px-16">
          <div className="mb-24">
            <div className="text-xs uppercase tracking-[2px] text-[#FF6B6B] mb-6">WHO WE ARE</div>
            <h2 className="text-6xl font-bold text-gray-900 leading-tight mb-8">
              <div>A dedicated team of experts</div>
              <div>developing <em className="font-serif not-italic">modern applications</em></div>
              <div>for the iOS platform</div>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl">
              SUALCAN SL specializes in creating innovative iOS applications that prioritize user security and experience. Our products, including VPN and Cleaner solutions, are meticulously crafted to enhance mobile device performance while maintaining the highest standards of privacy protection.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-8 mt-24">
            <div className="bg-gradient-to-br from-[#FF6B6B] to-[#FF8E53] rounded-3xl p-12 h-96 relative shadow-lg">
              <div className="absolute top-12 right-12 w-14 h-14 bg-white rounded-full flex items-center justify-center cursor-pointer hover:scale-110 transition-transform">
                <i className="ri-arrow-right-up-line text-2xl text-[#FF6B6B]"></i>
              </div>
              <div className="text-white">
                <div className="text-8xl font-serif font-bold mb-4">100%</div>
                <div className="text-2xl font-semibold mb-4">Security Priority</div>
                <div className="text-base text-white/80 max-w-xs">Every feature designed with user privacy first</div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#B8A4E8] to-[#9B7FD9] rounded-3xl p-12 h-96 relative shadow-lg">
              <div className="absolute top-12 right-12 w-14 h-14 bg-white rounded-full flex items-center justify-center cursor-pointer hover:scale-110 transition-transform">
                <i className="ri-arrow-right-up-line text-2xl text-[#9B7FD9]"></i>
              </div>
              <div className="text-white">
                <div className="text-8xl font-serif font-bold mb-4">iOS</div>
                <div className="text-2xl font-semibold mb-4">Platform Expertise</div>
                <div className="text-base text-white/80 max-w-xs">Native platform development ensuring optimal performance and seamless integration with Apple ecosystem</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-44 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-16">
          <div className="flex items-start justify-between mb-32">
            <div className="w-1/3">
              <div className="inline-block px-6 py-3 border border-white/30 rounded-full">
                <span className="text-sm text-white tracking-wide">OUR PRODUCTS</span>
              </div>
            </div>
            <div className="w-2/3">
              <h2 className="text-6xl font-bold text-white leading-tight">
                <div>Applications that enhance</div>
                <div>your mobile experience</div>
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-7">
            <div className="bg-[#F5F3EF] rounded-2xl overflow-hidden shadow-xl hover:-translate-y-2 transition-transform duration-300 cursor-pointer">
              <div className="h-72 bg-gradient-to-br from-blue-500 to-blue-700 relative overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center">
                  <img src="https://public.readdy.ai/ai/img_res/fa1d5b49d35b59a89854d6b8a42c0aa0.jpg" alt="VPN Application" className="w-full h-full object-cover object-top" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                  <h3 className="text-3xl font-bold text-white mb-2">VPN</h3>
                  <p className="text-base text-white/70">Secure connection protection</p>
                </div>
              </div>
            </div>

            <div className="bg-[#F5F3EF] rounded-2xl overflow-hidden shadow-xl hover:-translate-y-2 transition-transform duration-300 cursor-pointer">
              <div className="h-72 bg-gradient-to-br from-green-500 to-emerald-600 relative overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center">
                  <img src="https://readdy.ai/api/search-image?query=modern%20Cleaner%20application%20interface%20on%20iPhone%20screen%20with%20green%20gradient%20background%2C%20storage%20optimization%20display%2C%20clean%20files%20interface%2C%20minimal%20iOS%20design%2C%20professional%20app%20screenshot%20showing%20device%20cleaning%20features%2C%20high%20quality%20digital%20interface&width=340&height=288&seq=prod2&orientation=portrait" alt="Cleaner Application" className="w-full h-full object-cover object-top" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                  <h3 className="text-3xl font-bold text-white mb-2">Cleaner</h3>
                  <p className="text-base text-white/70">Device optimization tool</p>
                </div>
              </div>
            </div>

            <div className="bg-[#F5F3EF] rounded-2xl overflow-hidden shadow-xl hover:-translate-y-2 transition-transform duration-300 cursor-pointer">
              <div className="h-72 bg-gradient-to-br from-purple-600 to-indigo-700 relative overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center">
                  <img src="https://readdy.ai/api/search-image?query=modern%20Security%20Suite%20application%20interface%20on%20iPhone%20screen%20with%20purple%20gradient%20background%2C%20lock%20shield%20icon%2C%20security%20dashboard%2C%20privacy%20protection%20features%2C%20minimal%20iOS%20design%2C%20professional%20app%20screenshot%2C%20high%20quality%20digital%20interface&width=340&height=288&seq=prod3&orientation=portrait" alt="Security Suite" className="w-full h-full object-cover object-top" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                  <h3 className="text-3xl font-bold text-white mb-2">Security Suite</h3>
                  <p className="text-base text-white/70">Comprehensive protection</p>
                </div>
              </div>
            </div>

            <div className="bg-[#F5F3EF] rounded-2xl overflow-hidden shadow-xl hover:-translate-y-2 transition-transform duration-300 cursor-pointer">
              <div className="h-72 bg-gradient-to-br from-orange-500 to-red-600 relative overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center">
                  <img src="https://readdy.ai/api/search-image?query=modern%20Performance%20application%20interface%20on%20iPhone%20screen%20with%20orange%20gradient%20background%2C%20speed%20optimization%20dashboard%2C%20performance%20metrics%2C%20battery%20and%20memory%20stats%2C%20minimal%20iOS%20design%2C%20professional%20app%20screenshot%2C%20high%20quality%20digital%20interface&width=340&height=288&seq=prod4&orientation=portrait" alt="Performance Tools" className="w-full h-full object-cover object-top" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                  <h3 className="text-3xl font-bold text-white mb-2">Performance</h3>
                  <p className="text-base text-white/70">Speed optimization</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-36 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-16">
          <div className="flex gap-20">
            <div className="w-[45%]">
              <h2 className="text-6xl font-bold text-gray-900 leading-tight mb-10">
                <div>Get in touch with</div>
                <div>our team</div>
              </h2>
              <p className="text-lg text-gray-600 mb-16">
                We're here to answer questions about our iOS applications and development services
              </p>
              <div className="w-full h-80 rounded-2xl overflow-hidden">
                <img src="https://readdy.ai/api/search-image?query=modern%20professional%20office%20workspace%20with%20developers%20working%20on%20iOS%20applications%2C%20collaborative%20team%20environment%2C%20clean%20minimalist%20office%20design%2C%20natural%20lighting%2C%20technology%20company%20atmosphere%2C%20contemporary%20workspace%20with%20computers%20and%20mobile%20devices%2C%20professional%20business%20setting&width=480&height=320&seq=contact1&orientation=landscape" alt="SUALCAN SL Team" className="w-full h-full object-cover object-top" />
              </div>
            </div>

            <div className="w-[55%] grid grid-cols-2 gap-12">
              <div className="space-y-9">
                <div>
                  <div className="text-sm uppercase tracking-wider text-gray-500 mb-3">ADDRESS</div>
                  <div className="text-xl text-gray-900">CALLE EMILIO MUÑOZ 33</div>
                </div>
                <div>
                  <div className="text-sm uppercase tracking-wider text-gray-500 mb-3">CITY</div>
                  <div className="text-xl text-gray-900">MADRID</div>
                </div>
                <div>
                  <div className="text-sm uppercase tracking-wider text-gray-500 mb-3">ZIP CODE</div>
                  <div className="text-xl text-gray-900">28037</div>
                </div>
                <div>
                  <div className="text-sm uppercase tracking-wider text-gray-500 mb-3">COUNTRY</div>
                  <div className="text-xl text-gray-900">Spain</div>
                </div>
              </div>

              <div className="space-y-9">
                <div>
                  <div className="text-sm uppercase tracking-wider text-gray-500 mb-3">EMAIL</div>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-mail-line text-xl text-[#FF6B6B]"></i>
                    </div>
                    <div className="text-xl text-gray-900">contact@sualcansl.com</div>
                  </div>
                </div>
                <div>
                  <div className="text-sm uppercase tracking-wider text-gray-500 mb-3">PHONE</div>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-phone-line text-xl text-[#FF6B6B]"></i>
                    </div>
                    <div className="text-xl text-gray-900">+1 4472514616</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1E1E1E] py-20 px-20">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-start justify-between mb-16">
            <div className="flex items-center gap-3">
              <img src="https://public.readdy.ai/ai/img_res/85a73544-7acc-486b-93ea-d43e3a2ab79a.png" alt="SUALCAN SL Logo" className="h-8 w-8 object-contain" />
              <span className="text-xl text-white font-medium">SUALCAN SL</span>
            </div>
            <p className="text-base text-gray-400">Developing premium iOS applications since 2020</p>
          </div>

          <div className="grid grid-cols-4 gap-12 mb-20">
            <div>
              <h4 className="text-lg text-white font-medium mb-4">Products</h4>
              <div className="space-y-4">
                <div className="text-base text-gray-400 cursor-pointer hover:text-white transition-colors whitespace-nowrap">VPN Application</div>
                <div className="text-base text-gray-400 cursor-pointer hover:text-white transition-colors whitespace-nowrap">Cleaner Tool</div>
                <div className="text-base text-gray-400 cursor-pointer hover:text-white transition-colors whitespace-nowrap">Security Suite</div>
                <div className="text-base text-gray-400 cursor-pointer hover:text-white transition-colors whitespace-nowrap">Performance Tools</div>
              </div>
            </div>

            <div>
              <h4 className="text-lg text-white font-medium mb-4">Company</h4>
              <div className="space-y-4">
                <div className="text-base text-gray-400 cursor-pointer hover:text-white transition-colors whitespace-nowrap">About Us</div>
                <div className="text-base text-gray-400 cursor-pointer hover:text-white transition-colors whitespace-nowrap">Our Team</div>
                <div className="text-base text-gray-400 cursor-pointer hover:text-white transition-colors whitespace-nowrap">Careers</div>
                <div className="text-base text-gray-400 cursor-pointer hover:text-white transition-colors whitespace-nowrap">Press Kit</div>
              </div>
            </div>

            <div>
              <h4 className="text-lg text-white font-medium mb-4">Resources</h4>
              <div className="space-y-4">
                <div className="text-base text-gray-400 cursor-pointer hover:text-white transition-colors whitespace-nowrap">Documentation</div>
                <div className="text-base text-gray-400 cursor-pointer hover:text-white transition-colors whitespace-nowrap">Support Center</div>
                <div className="text-base text-gray-400 cursor-pointer hover:text-white transition-colors whitespace-nowrap">Privacy Policy</div>
                <div className="text-base text-gray-400 cursor-pointer hover:text-white transition-colors whitespace-nowrap">Terms of Service</div>
              </div>
            </div>

            <div className="text-right">
              <div className="text-lg font-serif text-white mb-2">© SUALCAN SL 2025</div>
              <div className="text-sm text-gray-400 mb-6">All rights reserved</div>
              <div className="flex justify-end gap-6">
                <span className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer underline whitespace-nowrap">LinkedIn</span>
                <span className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer underline whitespace-nowrap">Twitter</span>
                <span className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer underline whitespace-nowrap">GitHub</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-8 pt-20 border-t border-gray-800">
            <div className="w-1/4">
              <div className="w-full h-52 rounded-2xl overflow-hidden">
                <img src="https://readdy.ai/api/search-image?query=iPhone%20device%20displaying%20modern%20iOS%20application%20interface%20with%20security%20features%2C%20mobile%20app%20on%20screen%2C%20professional%20product%20photography%2C%20clean%20background%2C%20technology%20showcase%2C%20high%20quality%20device%20mockup&width=280&height=200&seq=footer1&orientation=landscape" alt="iOS Device" className="w-full h-full object-cover object-top" />
              </div>
            </div>

            <div className="w-5/12 pl-8">
              <div className="text-3xl font-serif text-white leading-relaxed">
                <div>Crafted for iOS.</div>
                <div>Built for security.</div>
                <div>Designed for you.</div>
              </div>
            </div>

            <div className="w-1/3">
              <p className="text-sm text-gray-400 mb-5 leading-relaxed">
                Stay updated with our latest releases and security updates
              </p>
              <div className="relative">
                <input 
                  type="email" 
                  placeholder="Enter your email" 
                  className="w-full h-12 px-5 pr-32 bg-transparent border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-gray-500 transition-colors"
                />
                <button className="absolute right-1 top-1 h-10 px-6 bg-[#0066FF] text-white rounded-lg flex items-center gap-2 hover:bg-[#0052CC] transition-colors whitespace-nowrap cursor-pointer">
                  <span className="text-sm font-medium">Subscribe</span>
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-800 text-center">
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-500 hover:text-gray-400 transition-colors whitespace-nowrap">
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
